import axios from "axios";
import t from "translations";
import confirm from "components/Dialog";
import { clone } from "underscore";
import { getProboSizeRange } from "utils/probo";
import { defaultSettings } from "utils/canvas";

const initClearCanvas = (canvas, w, h, settings = {}) => {
  canvas.clear();
  canvas.settings = {...defaultSettings, ...settings};
  canvas.DPE.cutZone.initBounds();
  canvas.DPE.cutZone.initClipZone();
  canvas.DPE.cutZone.setBackgroundColor("#fff");
  canvas.DPE.cutZone.updateSize(w, h, false, true);
  canvas.requestRenderAll();
  canvas.DPE.cutZone.fitCutZone();
};

const canvasStore = (store) => {
  store.on("@init", () => ({ canvas: null }));
  store.on("canvas/set", (_, canvas) => {
    return { canvas };
  });

  store.on("canvas/clear", async ({ canvas }) => {
    if (canvas?.DPE?.persistent?.isCanvasEmpty()) {
      return;
    }
    const result = await confirm.show({
      text: t.newCanvasWarn,
      title: t.newCanvasWarnTitle,
    });
    if (!result) {
      return;
    }
    initClearCanvas(canvas, canvas.cutW, canvas.cutH);
  });


  store.on("canvas/init", () => {
    store.dispatch("product/init");
    store.dispatch("content/setId", null);
    store.dispatch("content/setType", null);
    store.dispatch("product/setProboOptions", []);
    store.dispatch("product/setProboCodes", []);
    store.dispatch("product/setProboUploader", null);
    store.dispatch("notification/success", t.newCanvasCreated);
  });

  store.on("canvas/new", async ({ canvas }) => {
    const result = await confirm.show({
      text: t.newCanvasWarn,
      title: t.newCanvasWarnTitle,
    });
    if (!result) {
      return;
    }
    store.dispatch("canvas/init");
  });

  store.on("canvas/new/printplate", ({ canvas }) => {
    initClearCanvas(canvas, 3000, 3000, {
      maxWidth: 10000,
      maxHeight: 10000,
      cutEnabled: false,
      cutSize: 0
    });
    canvas.DPE.printplate.enable();
    return { id: null, editorType: "printplate" };
  });

  store.on("canvas/new/printfile", ({ canvas }) => {
    initClearCanvas(canvas, 700, 500);
    canvas.DPE.printplate.disable();
    return { id: null, editorType: "printfile" };
  });

  store.on("canvas/checkEditorType", ({ canvas }) => {
    if (canvas.hasCollage()) {
      store.dispatch("setEditorType/collage");
    } else {
      store.dispatch("setEditorType/printfile");
    }
  });

  const getScaleType = (editorType) => {
    switch (editorType) {
      case "collage":
        return "scale";
      case "printfile":
        return "fit";
      default:
        return "";
    }
  };

  store.on(
    "canvas/updateSize",
    async (
      { canvas, editorType, product, proboCodes, productOptions },
      { width, height }
    ) => {
      const { cutZone } = canvas.DPE;
      let w = width;
      let h = height;
      const scaleContentType = getScaleType(editorType);
      const isPrintplate = editorType === "printplate";
      try {
        if (product?.proboProduct) {
          const codes = clone(proboCodes);
          const width = codes.find((opt) => opt.code === "width");
          const height = codes.find((opt) => opt.code === "height");
          const sizeRange = getProboSizeRange(productOptions);
          console.log(sizeRange, w, h, width, height);
          if (width) {
            width.value = parseFloat(w, 10) / 10;
            if (sizeRange?.width) {
              if (width.value > sizeRange.width?.max) {
                width.value = sizeRange.width?.max;
              }
              if (width.value < sizeRange.width.min) {
                width.value = sizeRange.width.min;
              }
            }
          }
          if (height) {
            height.value = parseFloat(h, 10) / 10;
            if (sizeRange?.height) {
              if (height.value > sizeRange.height?.max) {
                height.value = sizeRange.height?.max;
              }
              if (height.value < sizeRange.height.min) {
                height.value = sizeRange.height.min;
              }
            }
          }
          const proboWidth = width?.value && width.value * 10;
          const proboHeight = height?.value && height.value * 10;
          cutZone.updateSize(
            proboWidth ?? w,
            proboHeight ?? h,
            scaleContentType,
            true
          );
          store.dispatch("product/setProboCodes", codes);
          const resp = await axios.post("/wp-json/dpe/v1/probo/product-options", {
            products: [
              {
                code: product?.linkedProboProduct,
                options: codes,
              },
            ],
          });
          const respProduct = resp.data.products[0];
          if (respProduct) {
            store.dispatch("product/setProboOptions", respProduct["selected_options"]);
            store.dispatch("product/setProboUploader", respProduct["uploaders"][0]);
          }
        } else {
          cutZone.updateSize(w, h, scaleContentType, isPrintplate);
        }
        cutZone.fitCutZone();
      } catch (error) {
        store.dispatch("notification/warning", error.message);
        const { minWidth, minHeight, maxWidth, maxHeight } = canvas.settings;
        if (width < minWidth * 10) {
          w = minWidth * 10;
        }
        if (width > maxWidth * 10) {
          w = maxWidth * 10;
        }
        if (height < minHeight * 10) {
          h = minHeight * 10;
        }
        if (height > maxHeight * 10) {
          h = maxHeight * 10;
        }
        cutZone.updateSize(w, h, scaleContentType);
        cutZone.fitCutZone();
        if (editorType === "printfile") {
          cutZone.fitContent();
        }
      }
    }
  );

  store.on("canvas/text/add", ({ canvas }) => {
    const scale = canvas.cutW / 500;
    const textbox = new window.fabric.IText(t.textboxInitialText, {
      left: Math.random() * (canvas.DPE.cutZone.width - 400),
      top: Math.random() * (canvas.DPE.cutZone.height - 150),
      fontFamily: "Roboto",
      fontWeight: 400,
      fill: "#cdcc32",
      textAlign: "center",
      scaleX: scale,
      scaleY: scale,
    });
    textbox.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
    });
    textbox.generateId();
    canvas.add(textbox);
    textbox.bringToFront();
    canvas.setActiveObject(textbox);
    store.dispatch("menu/setActiveMenu", 99);
  });

  let autosaveInterval;

  store.on("canvas/autosave/on", ({ canvas }) => {
    console.log("Turing on canvas autosave");
    autosaveInterval = setInterval(() => {
      const storage = canvas.DPE.persistent;
      const objs = canvas.getObjects().filter((o) => !o.excludeFromExport);
      if (objs?.length) {
        if (storage.toBase64() !== storage.getFromStorage()) {
          console.info("Saving to storage...");
          storage.saveToStorage();
        }
      } else {
        if (storage.isUnsavedDataExists()) {
          console.info("Clearing storage ...");
          storage.clearStorage();
        }
      }
    }, 3000);
  });

  store.on("canvas/autosave/off", () => {
    clearInterval(autosaveInterval);
  });
};

export default canvasStore;
